// src/encounter/structured-note/chart-readout-parts.tsx
//
// Chart readout (switch `chartReadout`; Antonin's catalog ChartReadout, scrips-signal-ds feat/signal @166c3c35): the picker "\"
// opens under a note line, and the read-only readout a pick inserts. Display only: the rows come from lib/chart-readout.ts, the
// line (inline-note-line.tsx) owns the keyboard and the save. Tokens only.
//
// The catalog's look: the picker is a raised menu under the field (one row per source, its title and its kind and date); the
// readout is a grey inner card with its title, "<kind · date> · from the chart" and an X, then the values. A lab set lists one
// row per result (name and range, value and unit, the lab's flag as the Labs tab shows it) with a hairline between; the vitals sit in a 4-column grid.
import { X } from 'lucide-react';
import { Button } from '@scripsteam/scrips-signal-ds';
import { V2_META } from '../lib/design-v2';
import { readoutMeta, type ReadoutSource } from '../lib/chart-readout';
import { chartName, type ChartPiece, type ChartResult } from '../lib/note-pieces';
import { labFlag } from '@/patients/lib/lab-results';
import { FlagChip } from '@/patients/components/chart/labs/lab-analyte-row';

export const CHART_READOUT_MENU_LABEL = 'Chart data';
export const CHART_READOUT_FROM = 'from the chart';

export function ChartReadoutMenu({
  id,
  sources,
  highlighted,
  onPick,
  onHighlight,
}: {
  id: string;
  sources: readonly ReadoutSource[];
  highlighted: number;
  onPick: (source: ReadoutSource) => void;
  onHighlight: (index: number) => void;
}) {
  return (
    <ul
      id={id}
      role="listbox"
      aria-label={CHART_READOUT_MENU_LABEL}
      data-chart-readout-menu=""
      className="m-0 flex w-full max-w-80 list-none flex-col rounded-md border border-border-default bg-surface-card p-1 shadow-overlay"
    >
      {sources.map((s, i) => (
        <li
          key={s.query}
          role="option"
          aria-selected={i === highlighted}
          className={`flex min-h-8 cursor-pointer items-baseline justify-between gap-2 rounded-md px-3 py-1 ${i === highlighted ? 'bg-surface-panel' : ''}`}
          // Before the field's blur, so the pick lands and the line keeps focus (the catalog's onMouseDown).
          onMouseDown={(e) => {
            e.preventDefault();
            onPick(s);
          }}
          onMouseEnter={() => onHighlight(i)}
        >
          <span className="font-sans text-body-15 text-text-primary">{s.title}</span>
          <span className={V2_META}>{s.meta}</span>
        </li>
      ))}
    </ul>
  );
}

export function ChartReadoutCard({ piece, onRemove }: { piece: ChartPiece; onRemove?: () => void }) {
  const title = chartName(piece.query, piece.label);
  const results = piece.snapshot?.results ?? [];
  const meta = piece.snapshot ? readoutMeta(piece.snapshot) : '';
  return (
    <section aria-label={`From the chart: ${title}`} data-chart-readout={piece.query} className="flex flex-col gap-1 rounded-md bg-surface-panel px-3 pt-2 pb-1">
      <header className="flex min-h-8 items-center gap-2">
        <span className="font-sans text-body-15 font-medium text-text-primary">{title}</span>
        <span className={`${V2_META} min-w-0 flex-1`}>
          {meta ? `${meta} · ` : ''}
          {CHART_READOUT_FROM}
        </span>
        {onRemove ? (
          <Button variant="tertiary" size="sm" icon={<X className="size-4" aria-hidden />} aria-label={`Remove ${title} from the note`} onClick={onRemove} />
        ) : null}
      </header>
      {piece.query === 'vitals.latest' ? (
        <ul className="m-0 grid list-none grid-cols-4 gap-x-4 gap-y-2 p-0 pb-2">
          {results.map((r, i) => (
            <li key={r.sourceId ?? i} className="flex flex-col">
              <span className={V2_META}>{r.label ?? r.display}</span>
              {r.label ? <span className="font-sans text-body-15 font-medium text-text-primary tabular-nums">{valueText(r)}</span> : null}
            </li>
          ))}
        </ul>
      ) : (
        <ul className="m-0 flex list-none flex-col divide-y divide-border-default p-0">
          {results.map((r, i) => (
            <li key={r.sourceId ?? `${r.code ?? r.label}-${i}`} className="flex items-center justify-between gap-4 py-2">
              <span className="flex min-w-0 flex-col">
                <span className="font-sans text-body-15 text-text-primary">{r.label ?? r.display}</span>
                {r.range ? <span className={V2_META}>Range {r.range}{r.unit ? ` ${r.unit}` : ''}</span> : null}
              </span>
              <span className="flex shrink-0 items-baseline gap-2">
                <span className="font-sans text-body-15 text-text-primary tabular-nums">
                  <b className="font-medium">{r.value ?? ''}</b>
                  {r.unit ? ` ${r.unit}` : ''}
                </span>
                {/* The Labs tab's flag (▲ High, red when critical); N, or a code it does not know, shows none. */}
                <FlagChip flag={labFlag(r.flag)} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function valueText(r: ChartResult): string {
  return `${r.value ?? ''}${r.unit ? ` ${r.unit}` : ''}`;
}
